import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Text, Group, Stack as MantineStack, Button, Box, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Game, Player, Stack } from '../types/models';
import { apiClient } from '../services/api';
import SVGDrawingBoard, { SVGDrawingBoardHandle } from './SVGDrawingBoard';
import { DrawingControlsMantine } from './DrawingControlsMantine';
import { GameLayout } from './layouts/GameLayout';
import { useSquareCanvas } from '../hooks/useSquareCanvas';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useDrawingPersistence } from '../hooks/useDrawingPersistence';
import { usePreventGestures } from '../hooks/usePreventGestures';
import { AVATAR_FULL_SIZE, createOpaquePngFromBlob } from '../utils/avatarImages';
import { DrawingData, DrawingTool } from '../utils/drawingExport';

interface DrawPromptProps {
  game: Game;
  currentPlayer: Player;
  stack: Stack;
  prompt: string;
  entryNumber: number;
  turnsRemaining: number;
  onDrawingSubmitted: () => void;
  onReturnToLobby?: () => void;
}

const DrawPrompt: React.FC<DrawPromptProps> = ({
  game,
  currentPlayer,
  stack,
  prompt,
  entryNumber,
  turnsRemaining,
  onDrawingSubmitted,
  onReturnToLobby
}) => {
  const { isMobile } = useResponsiveLayout();
  const { width: canvasSize } = useSquareCanvas({
    padding: isMobile ? 8 : 16,
    reservedTop: isMobile ? 160 : 140,
    reservedBottom: isMobile ? 160 : 120,
    maxSize: isMobile ? 600 : 800,
  });

  const mostRecentEntry = stack.entries[stack.entries.length - 1];
  const promptAuthor = mostRecentEntry
    ? game.players.find(p => p.id === mostRecentEntry.playerId)
    : null;
  const promptFont = promptAuthor?.handwritingFont || 'Caveat';
  
  // Helper to render progress dots
  const renderProgressDots = (): string => {
    const totalEntries = game.totalEntriesPerStack || stack.expectedEntries.length;
    const completed = '●'.repeat(entryNumber - 1);
    const remaining = '○'.repeat(totalEntries - entryNumber + 1);
    return completed + remaining;
  };
  
  // State declarations
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentTool, setCurrentTool] = useState<DrawingTool>('pen');
  const [currentSize, setCurrentSize] = useState(5);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drawingBoardRef = useRef<SVGDrawingBoardHandle | null>(null);
  const submitLockRef = useRef(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const strokeCountRef = useRef(0);
  const [hasChanges, setHasChanges] = useState(false);
  const gestureContainerRef = useRef<HTMLDivElement | null>(null);
  const { loadDrawing, saveDrawing, clearDrawing } = useDrawingPersistence({
    gameKey: game.key,
    stackId: stack.id,
    entryNumber
  });
  usePreventGestures(gestureContainerRef);
  const handleStrokeCountChange = useCallback((count: number) => {
    strokeCountRef.current = count;
    setHasChanges(count > 0 && !hasSubmitted);
    if (count > 0 && error) {
      setError(null);
    }
  }, [error, hasSubmitted]);
  const handlePersist = useCallback((drawingData: DrawingData | null) => {
    if (hasSubmitted) return;

    if (drawingData) {
      saveDrawing(drawingData);
    } else {
      clearDrawing();
    }
  }, [clearDrawing, hasSubmitted, saveDrawing]);

  useEffect(() => {
    let isActive = true;

    const restoreDrawing = async () => {
      const saved = await loadDrawing();
      if (!isActive) return;
      // Don't clobber strokes the player drew while the load was in flight
      if (strokeCountRef.current > 0) return;
      if (saved) {
        drawingBoardRef.current?.loadDrawingData(saved);
      } else {
        drawingBoardRef.current?.clear();
      }
    };

    restoreDrawing();

    return () => {
      isActive = false;
    };
  }, [loadDrawing]);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    const originalContent = viewport.getAttribute('content');

    viewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    );

    return () => {
      if (originalContent) {
        viewport.setAttribute('content', originalContent);
      } else {
        viewport.removeAttribute('content');
      }
    };
  }, []);

  const flushPersistedDrawing = useCallback(() => {
    if (hasSubmitted) return;
    const drawingData = drawingBoardRef.current?.getDrawingData();
    if (drawingData) {
      saveDrawing(drawingData);
    }
  }, [hasSubmitted, saveDrawing]);

  // Add navigation guard to prevent accidental back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges && !hasSubmitted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, hasSubmitted]);

  useEffect(() => {
    const handlePageHide = () => {
      flushPersistedDrawing();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushPersistedDrawing();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flushPersistedDrawing]);

  const handleSubmitWithFinalSave = useCallback(() => {
    if (submitLockRef.current || isSubmitting || hasSubmitted) return;

    if (strokeCountRef.current < 1) {
      const message = 'Please add a drawing before submitting.';
      setError(message);
      notifications.show({
        title: 'Drawing required',
        message: 'Draw something before passing it on.',
        color: 'red',
      });
      return;
    }

    if (!drawingBoardRef.current) {
      setError('Drawing board is not ready. Please try again.');
      return;
    }
    
    // Trigger a final save which will call handleDrawingSubmit
    // handleDrawingSubmit uploads the image AND submits the stack entry
    submitLockRef.current = true;
    setIsSubmitting(true);
    setError(null);
    drawingBoardRef.current.handleSave().then((saved) => {
      if (saved) return;
      submitLockRef.current = false;
      setIsSubmitting(false);
      setError('Failed to save your drawing. Please try again.');
      notifications.show({
        title: 'Failed to save drawing',
        message: 'Unable to export your drawing. Please try again.',
        color: 'red',
      });
    });
  }, [isSubmitting, hasSubmitted]);

  const handleDrawingSubmit = useCallback(async (blob: Blob) => {
    setIsSubmitting(true);
    setError(null);

    try {
      console.log('Uploading drawing...');
      // Upload the white-flattened PNG as the primary content: the
      // transparent export renders as a partial image on dark surfaces
      // (e.g. Discord embeds) whenever a viewer composites it directly
      const opaqueBlob = await createOpaquePngFromBlob(blob).catch((error) => {
        console.warn('Failed to flatten drawing, uploading transparent PNG:', error);
        return blob;
      });
      const uploadResult = await apiClient.uploadDrawing(opaqueBlob);
      console.log('Drawing uploaded:', uploadResult);

      console.log('Creating stack entry...', {
        gameKey: game.key,
        stackId: stack.id,
        type: 'drawing',
        content: uploadResult.data.url,
        contentOpaque: uploadResult.data.url
      });

      await apiClient.createStackEntry(game.key, stack.id, {
        type: 'drawing',
        content: uploadResult.data.url,
        contentOpaque: uploadResult.data.url
      });

      console.log('Stack entry created successfully');
      setHasSubmitted(true);
      setHasChanges(false);
      await clearDrawing();
      onDrawingSubmitted();
    } catch (err) {
      console.error('Failed to submit drawing:', err);
      setError('Failed to submit drawing. Please try again.');
      notifications.show({
        title: 'Failed to submit drawing',
        message: 'Unable to save your drawing. Please try again.',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  }, [clearDrawing, game.key, stack.id, onDrawingSubmitted]);

  const handleReturnToLobby = useCallback(() => {
    flushPersistedDrawing();
    onReturnToLobby?.();
  }, [flushPersistedDrawing, onReturnToLobby]);

  // Stable handlers keep the memoized drawing controls from re-rendering when
  // poll refetches re-render this component with fresh game/stack objects
  const handleUndo = useCallback(() => drawingBoardRef.current?.handleUndo(), []);
  const handleRedo = useCallback(() => drawingBoardRef.current?.handleRedo(), []);
  const handleClear = useCallback(() => {
    drawingBoardRef.current?.clear();
    setCanUndo(false);
    setCanRedo(false);
    setError(null);
  }, []);

  const returnButton = onReturnToLobby ? (
    <Button
      size={isMobile ? "xs" : "sm"}
      variant="light"
      onClick={handleReturnToLobby}
      disabled={isSubmitting}
    >
      Save & lobby
    </Button>
  ) : null;

  const passButton = (
    <Button
      size={isMobile ? "sm" : "md"}
      onClick={handleSubmitWithFinalSave}
      disabled={isSubmitting}
      loading={isSubmitting}
      variant={isMobile ? "light" : "filled"}
      style={{ backgroundColor: '#0da388', color: 'white' }}
    >
      Pass it!
    </Button>
  );

  const leftSlot = returnButton || <Box style={{ width: isMobile ? 84 : 110 }} />;

  // Header content
  const header = (
    <Box style={{ width: '100%' }}>
      <MantineStack gap="xs" align="center">
        {/* Progress Dots */}
        <Group gap="xs" justify="center">
          <Text size={isMobile ? "md" : "lg"} fw={500}>
            {renderProgressDots()}
          </Text>
          <Text size="sm" c="dimmed">
            {entryNumber} of {game.totalEntriesPerStack || stack.expectedEntries.length}
          </Text>
        </Group>

        <Group gap="md" justify="space-between" align="flex-start" wrap="nowrap" w="100%">
          <Box style={{ flexShrink: 0 }}>
            {leftSlot}
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <MantineStack gap={4} align="center">
              <Title order={isMobile ? 4 : 3} ta="center">
                Draw it!
              </Title>
              <Text
                size={isMobile ? "lg" : "xl"}
                fw={500}
                ta="center"
                style={{
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  fontFamily: `"${promptFont}", var(--mantine-font-family)`
                }}
              >
                {prompt}
              </Text>
            </MantineStack>
          </Box>
          <Box style={{ flexShrink: 0 }}>
            {passButton}
          </Box>
        </Group>
      </MantineStack>
    </Box>
  );

  // Main content (drawing board)
  const content = (
    <Box style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {canvasSize > 0 && (
        <Box style={{ width: `${canvasSize}px`, height: `${canvasSize}px` }}>
          <SVGDrawingBoard
            key={`${stack.id}-${entryNumber}`}
            ref={drawingBoardRef}
            width={AVATAR_FULL_SIZE}
            height={AVATAR_FULL_SIZE}
            currentColor={currentColor}
            currentTool={currentTool}
            strokeWidth={currentSize}
            onCanUndoChange={setCanUndo}
            onCanRedoChange={setCanRedo}
            onSave={handleDrawingSubmit}
            onStrokeCountChange={handleStrokeCountChange}
            onPersist={handlePersist}
          />
        </Box>
      )}
      {error && (
        <Text c="red" ta="center" mt="sm">
          {error}
        </Text>
      )}
    </Box>
  );

  // Controls - just the drawing controls now
  const controls = (
    <DrawingControlsMantine
      currentColor={currentColor}
      currentTool={currentTool}
      currentSize={currentSize}
      onColorChange={setCurrentColor}
      onSizeChange={setCurrentSize}
      onToolChange={setCurrentTool}
      onUndo={handleUndo}
      onRedo={handleRedo}
      canUndo={canUndo}
      canRedo={canRedo}
      onClear={handleClear}
      showEraser
    />
  );

  return (
    <Box
      ref={gestureContainerRef}
      style={{
        overscrollBehavior: 'contain',
        overscrollBehaviorX: 'none',
      }}
    >
      <GameLayout
        mode="draw"
        header={header}
        content={content}
        controls={controls}
      />
    </Box>
  );
};

export default DrawPrompt; 
